type Properties = Record<string, string | number | boolean>;
const FIRST_TOUCH_KEY = "stencil-studio:analytics-first-touch:v1";
const SESSION_KEY = "stencil-studio:analytics-session:v1";
const SESSION_TIMEOUT = 30 * 60 * 1000;
const CAMPAIGN_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const PAGES: Record<string, string> = {
  "/": "landing", "/lp-2": "landing_experiment_lp_2", "/ai": "studio", "/ai-2": "studio", "/ai-3": "studio", "/ai-4": "studio",
  "/sign-up": "sign_up", "/sign-in": "sign_in", "/billing": "billing",
  "/forgot-password": "forgot_password", "/reset-password": "reset_password",
  "/privacy": "privacy", "/privacy-policy": "privacy", "/terms": "terms",
  "/terms-and-conditions": "terms", "/refund-policy": "refund_policy",
};

export function pageContext(url: URL): { $current_url: string; page_path: string; page_name: string } {
  // Unknown paths can contain user identifiers. Queries and hashes can contain credentials.
  const path = Object.hasOwn(PAGES, url.pathname) ? url.pathname : "/other";
  return { $current_url: `${url.origin}${path}`, page_path: path, page_name: PAGES[path] ?? "other" };
}

function acquisition(url: URL, referrer: string): Properties {
  const properties: Properties = { landing_page: pageContext(url).page_path, referring_domain: "direct" };
  try {
    const referring = new URL(referrer);
    if (referring.host !== url.host) properties.referring_domain = referring.hostname;
  } catch { /* No external referrer. */ }
  for (const key of CAMPAIGN_KEYS) {
    const value = url.searchParams.get(key);
    // Campaign labels only: never arbitrary URLs, emails, or free-form text.
    if (value && /^[a-zA-Z0-9][a-zA-Z0-9 _.-]{0,99}$/.test(value)) properties[key] = value;
  }
  return properties;
}

function read(storage: "localStorage" | "sessionStorage", key: string): Properties | null {
  try {
    const value: unknown = JSON.parse(window[storage].getItem(key) ?? "null");
    if (value && typeof value === "object" && !Array.isArray(value) &&
      Object.values(value).every(item => ["string", "number", "boolean"].includes(typeof item))) return value as Properties;
  } catch { /* Storage may be unavailable. */ }
  return null;
}

function write(storage: "localStorage" | "sessionStorage", key: string, value: Properties | null) {
  try {
    if (value) window[storage].setItem(key, JSON.stringify(value));
    else window[storage].removeItem(key);
  } catch { /* In-memory context still works. */ }
}

// Constructing this captures the entry page in memory only. Persistence begins with a tracked event.
export function createEventContext() {
  let entry = acquisition(new URL(window.location.href), document.referrer);
  let first: Properties | null = null;
  let session: Properties | null = null;
  return {
    capture(): Properties {
      const url = new URL(window.location.href);
      const now = Date.now();
      first ??= read("localStorage", FIRST_TOUCH_KEY) ?? entry;
      write("localStorage", FIRST_TOUCH_KEY, first);
      session ??= read("sessionStorage", SESSION_KEY);
      if (!session || typeof session.session_id !== "string" || typeof session.last_activity !== "number" || now - session.last_activity >= SESSION_TIMEOUT) {
        const touch = session ? acquisition(url, "") : entry;
        session = { ...touch, session_id: crypto.randomUUID(), last_activity: now };
      }
      session.last_activity = now;
      write("sessionStorage", SESSION_KEY, session);
      const context: Properties = { ...pageContext(url), session_id: String(session.session_id), event_schema_version: 1 };
      for (const [key, value] of Object.entries(first)) context[`first_touch_${key}`] = value;
      for (const [key, value] of Object.entries(session)) {
        if (key !== "session_id" && key !== "last_activity") context[`session_${key}`] = value;
      }
      return context;
    },
    clear() {
      first = null;
      session = null;
      entry = acquisition(new URL(window.location.href), "");
      write("localStorage", FIRST_TOUCH_KEY, null);
      write("sessionStorage", SESSION_KEY, null);
    },
  };
}
